import { Select, TextInput } from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import {
  HAS_INSURANCE,
  healthCareProviderOptions,
  healthCareSystemOptions,
  healthInsuranceOptions,
  HEALTH_CARE_OTHER,
  ISAPRE,
} from '../../../utils/selectOptions';
import SectionTitle from '../../Text/SectionTitle';

type ContactSectionProps = {
  prefix: string;
  fieldNames?: {
    healthCareSystem: string;
    healthCareProvider: string;
    otherHealthCare: string;
    healthCareContributorID: string;
    healthInsurance: string;
    healthInsuranceContributorID: string;
    phoneNumberHealthCare: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DymanicHealthCareCampus = ({
  prefix,
  fieldNames = {
    healthCareSystem: 'healthCareSystem',
    healthCareProvider: 'healthCareProvider',
    otherHealthCare: 'otherHealthCare',
    healthCareContributorID: 'healthCareContributorID',
    healthInsurance: 'healthInsurance',
    healthInsuranceContributorID: 'healthInsuranceContributorID',
    phoneNumberHealthCare: 'phoneNumberHealthCare',
  },
  disabled = false,
}: ContactSectionProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );
  const { control, watch } = useFormContext();
  const selectedHealthCareSystem = watch(fieldNames.healthCareSystem);
  const selectedHealthInsurance = watch(fieldNames.healthInsurance);

  return (
    <>
      <Row className="pt-5">
        <Col className="pb-3" xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.healthCareSystem}
            label={t(`${prefix}.${fieldNames.healthCareSystem}`)}
            options={healthCareSystemOptions}
            isSearchable={false}
            control={control}
            disabled={disabled}
          />
        </Col>
        {selectedHealthCareSystem?.value === ISAPRE && (
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.healthCareProvider}
              label={t(`${prefix}.${fieldNames.healthCareProvider}`)}
              options={healthCareProviderOptions}
              isSearchable={false}
              control={control}
              disabled={disabled}
              shouldUnregister
            />
          </Col>
        )}
        {selectedHealthCareSystem?.value === HEALTH_CARE_OTHER && (
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.otherHealthCare}
              label={t(`${prefix}.${fieldNames.otherHealthCare}`)}
              control={control}
              disabled={disabled}
              shouldUnregister
            />
          </Col>
        )}
        {selectedHealthCareSystem?.value && (
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.healthCareContributorID}
              label={t(`${prefix}.${fieldNames.healthCareContributorID}`)}
              control={control}
              disabled={disabled}
              formatter="rut"
              shouldUnregister
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.healthInsurance}
            label={t(`${prefix}.${fieldNames.healthInsurance}`)}
            options={healthInsuranceOptions}
            control={control}
            disabled={disabled}
            isSearchable={false}
          />
        </Col>
        {selectedHealthInsurance?.value === HAS_INSURANCE && (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.healthInsuranceContributorID}
                label={t(
                  `${prefix}.${fieldNames.healthInsuranceContributorID}`,
                )}
                control={control}
                disabled={disabled}
                formatter="rut"
                shouldUnregister
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.phoneNumberHealthCare}
                label={t(`${prefix}.${fieldNames.phoneNumberHealthCare}`)}
                tooltip={t(`${prefix}.tooltipPhoneNumberHealthCare`)}
                control={control}
                disabled={disabled}
                formatter="phone"
                shouldUnregister
              />
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default DymanicHealthCareCampus;
